import React, { memo, useEffect, useRef, useState } from 'react';

import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Box, Chip, IconButton, Stack, Typography } from '@mui/material';

interface JsonTreeViewProps {
	data: unknown;
	expandAllSignal: number;
	collapseAllSignal: number;
}

interface JsonTreeValueProps extends JsonTreeViewProps {
	label?: string;
	level?: number;
}

function getPrimitiveColor(value: unknown): string {
	if (value === null || value === undefined) {
		return 'text.disabled';
	}

	switch (typeof value) {
		case 'boolean':
			return 'success.main';
		case 'number':
			return 'secondary.main';
		case 'string':
			return 'info.main';
		default:
			return 'text.primary';
	}
}

function JsonTreeValue({ label, data, level = 0, expandAllSignal, collapseAllSignal }: JsonTreeValueProps) {
	const [expanded, setExpanded] = useState(true);
	const previousExpandAllSignal = useRef(expandAllSignal);
	const previousCollapseAllSignal = useRef(collapseAllSignal);
	const isObject = typeof data === 'object' && data !== null;
	const isArray = Array.isArray(data);
	const entries = isObject ? (isArray ? (data as unknown[]).map((item, index) => [String(index), item] as const) : Object.entries(data as Record<string, unknown>)) : [];
	const childCount = entries.length;

	useEffect(() => {
		if (previousExpandAllSignal.current === expandAllSignal) {
			return;
		}

		previousExpandAllSignal.current = expandAllSignal;
		setExpanded(true);
	}, [expandAllSignal]);

	useEffect(() => {
		if (previousCollapseAllSignal.current === collapseAllSignal) {
			return;
		}

		previousCollapseAllSignal.current = collapseAllSignal;
		setExpanded(false);
	}, [collapseAllSignal]);

	if (!isObject) {
		return (
			<Stack direction="row" spacing={0.75} alignItems="baseline" sx={{ minWidth: 0 }}>
				{label !== undefined && (
					<Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', flexShrink: 0 }}>
						{label}
					</Typography>
				)}
				<Typography
					variant="body2"
					sx={{
						fontFamily: 'monospace',
						fontSize: '0.75rem',
						wordBreak: 'break-word',
						whiteSpace: 'pre-wrap',
						color: getPrimitiveColor(data),
					}}
				>
					{data === null || data === undefined ? 'null' : String(data)}
				</Typography>
			</Stack>
		);
	}

	return (
		<Box sx={{ minWidth: 0 }}>
			<Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
				{childCount > 0 && (
					<IconButton size="small" onClick={() => setExpanded((value) => !value)} sx={{ p: 0.125 }}>
						{expanded ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
					</IconButton>
				)}
				{label !== undefined && (
					<Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', flexShrink: 0 }}>
						{label}
					</Typography>
				)}
				<Chip
					size="small"
					label={isArray ? `Array(${childCount})` : `Object(${childCount})`}
					sx={{
						height: 18,
						borderRadius: 1,
						fontFamily: 'monospace',
						fontSize: '0.65rem',
						color: 'text.secondary',
						bgcolor: 'transparent',
						opacity: 0.75,
						'& .MuiChip-label': { px: 0.75 },
					}}
				/>
			</Stack>
			{childCount > 0 && expanded && (
				<Stack
					spacing={0.25}
					sx={{
						position: 'relative',
						mt: 0.25,
						ml: level === 0 && label === undefined ? 0 : 1.5,
						pl: 1.5,
						borderLeft: '1px solid',
						borderColor: 'divider',
					}}
				>
					{entries.map(([childLabel, childValue]) => (
						<Box
							key={childLabel}
							sx={{
								position: 'relative',
								'&::before': {
									content: '""',
									position: 'absolute',
									left: -12,
									top: 12,
									width: 8,
									borderTop: '1px solid',
									borderColor: 'divider',
								},
							}}
						>
							<JsonTreeValue
								label={isArray ? `[${childLabel}]` : childLabel}
								data={childValue}
								level={level + 1}
								expandAllSignal={expandAllSignal}
								collapseAllSignal={collapseAllSignal}
							/>
						</Box>
					))}
				</Stack>
			)}
		</Box>
	);
}

function JsonTreeView({ data, expandAllSignal, collapseAllSignal }: JsonTreeViewProps) {
	return <JsonTreeValue data={data} expandAllSignal={expandAllSignal} collapseAllSignal={collapseAllSignal} />;
}

export default memo(JsonTreeView);
